import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabase';

interface JobRequest {
  id: string;
  user_id: string;
  title: string;
  description: string;
  category: string;
  subcategory: string;
  location: string;
  budget: string;
  budget_type: string;
  priority: string;
  urgency: string;
  duration: string;
  skills: string[];
  photos: string[];
  payment_method: string;
  experience: string;
  availability: string;
  response_time: string;
  condition: string;
  publish_to: string;
  accept_cash: boolean;
  accept_secure: boolean;
  created_at: string;
  scheduled_at: string;
  profile_type: string;
  status: string;
  currency_code: string;
  latitude: number;
  longitude: number;
}

interface ServiceProviderOffer {
  id: string;
  job_id: string;
  provider_id: string;
  customer_id: string;
  offer_amount: string;
  offer_type: string;
  estimated_hours: number | null;
  start_date: string;
  completion_date: string | null;
  message: string;
  includes_materials: boolean;
  materials_cost: string | null;
  timeline: string;
  working_hours: string;
  guarantee_period: string;
  additional_services: string[];
  status: string;
  total_amount: string;
  created_at: string;
  updated_at: string;
  provider_profile_type: string;
  provider_business_name: string | null;
  provider_first_name: string;
  provider_last_name: string;
  provider_fee: string;
  requester_fee: string;
  total_offer_amount: string;
  payment_intent_id: string | null;
  client_secret: string | null;
  cancellation_reason: string | null;
  payment_captured_at: string | null;
  offer_payment_type: string;
  jobRequest?: {
    id: string;
    title: string;
    user_id: string;
    budget: string;
    currency_code: string;
    budget_type: string;
    location: string;
  };
}

interface JobServiceEscrowPayment {
  id: string;
  offer_id: string;
  payment_intent_id: string;
  amount_captured: string;
  offer_amount: string;
  requester_fee: string;
  provider_fee: string;
  payout_amount: string;
  stripe_fee: string;
  platform_fee: string;
  platform_revenue: string;
  transfer_id: string | null;
  transfer_created_at: string | null;
  payment_authorised_at: string;
  cancelled_at: string | null;
  status: string;
  released_at: string | null;
  refunded_at: string | null;
  created_at: string;
  updated_at: string;
  currency_code?: string;
  receipt_url?: string;
  risk_score?: number;
  risk_level?: string;
  authentication_method?: string;
  payment_method_type?: string;
  card_brand?: string;
  requester_location_name?: string;
  requester_latitude?: number;
  requester_longitude?: number;
  provider_location_name?: string;
  provider_latitude?: number;
  provider_longitude?: number;
  new_payment_intent_id?: string | null;
}

const UserJobsServices: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState<'listings' | 'rentals'>('listings');
  const [jobRequests, setJobRequests] = useState<JobRequest[]>([]);
  const [serviceOffers, setServiceOffers] = useState<ServiceProviderOffer[]>([]);
  const [escrowPayments, setEscrowPayments] = useState<JobServiceEscrowPayment[]>([]);
  const [expandedOffers, setExpandedOffers] = useState<Set<string>>(new Set());
  const [expandedJobs, setExpandedJobs] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userEmail, setUserEmail] = useState<string>('');
  const [userDisplayName, setUserDisplayName] = useState<string>('');
  
  // Search states for each tab
  const [listingsSearchTerm, setListingsSearchTerm] = useState('');
  const [jobsSearchTerm, setJobsSearchTerm] = useState('');
  const [filteredJobRequests, setFilteredJobRequests] = useState<JobRequest[]>([]);
  const [filteredServiceOffers, setFilteredServiceOffers] = useState<ServiceProviderOffer[]>([]);

  useEffect(() => {
    if (userId) {
      fetchUserData();
    }
  }, [userId]);

  // Filter job requests based on search term
  useEffect(() => {
    if (listingsSearchTerm.trim() === '') {
      setFilteredJobRequests(jobRequests);
    } else {
      const filtered = jobRequests.filter(job => 
        (job.title && typeof job.title === 'string' && job.title.toLowerCase().includes(listingsSearchTerm.toLowerCase())) ||
        (job.category && typeof job.category === 'string' && job.category.toLowerCase().includes(listingsSearchTerm.toLowerCase())) ||
        (job.subcategory && typeof job.subcategory === 'string' && job.subcategory.toLowerCase().includes(listingsSearchTerm.toLowerCase())) ||
        (job.location && typeof job.location === 'string' && job.location.toLowerCase().includes(listingsSearchTerm.toLowerCase())) ||
        (job.status && typeof job.status === 'string' && job.status.toLowerCase().includes(listingsSearchTerm.toLowerCase())) ||
        (job.budget && job.budget.toString().toLowerCase().includes(listingsSearchTerm.toLowerCase()))
      );
      setFilteredJobRequests(filtered);
    }
  }, [listingsSearchTerm, jobRequests]);

  // Filter service offers based on search term
  useEffect(() => {
    if (jobsSearchTerm.trim() === '') {
      setFilteredServiceOffers(serviceOffers);
    } else {
      const filtered = serviceOffers.filter(offer => 
        (offer.jobRequest?.title && typeof offer.jobRequest.title === 'string' && offer.jobRequest.title.toLowerCase().includes(jobsSearchTerm.toLowerCase())) ||
        (offer.status && typeof offer.status === 'string' && offer.status.toLowerCase().includes(jobsSearchTerm.toLowerCase())) ||
        (offer.customer_id && typeof offer.customer_id === 'string' && offer.customer_id.toLowerCase().includes(jobsSearchTerm.toLowerCase())) ||
        (offer.provider_id && typeof offer.provider_id === 'string' && offer.provider_id.toLowerCase().includes(jobsSearchTerm.toLowerCase())) ||
        (offer.offer_amount && offer.offer_amount.toString().toLowerCase().includes(jobsSearchTerm.toLowerCase()))
      );
      setFilteredServiceOffers(filtered);
    }
  }, [jobsSearchTerm, serviceOffers]);

  const fetchUserData = async () => {
    try {
      setLoading(true);
      setError(null);



      // Get user email first
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        setError('No active session');
        return;
      }

      // Fetch user profile to get email and name
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('email, first_name, last_name, business_name, profile_type')
        .eq('id', userId)
        .single();

      if (profile?.email) {
        setUserEmail(profile.email);
      }

      // Set display name (business name for business profiles, first + last name for individuals)
      if (profile?.profile_type === 'business' && profile?.business_name) {
        setUserDisplayName(profile.business_name);
      } else if (profile?.first_name || profile?.last_name) {
        const firstName = profile?.first_name || '';
        const lastName = profile?.last_name || '';
        setUserDisplayName(`${firstName} ${lastName}`.trim());
      } else {
        setUserDisplayName(`User ID: ${userId}`);
      }

      // Fetch user's job requests (User Listings)
      const { data: jobRequestsData, error: jobRequestsError } = await supabase
        .from('jobrequests')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (jobRequestsError) {
        // Error fetching job requests
      } else {
        setJobRequests(jobRequestsData || []);
      }

            // Fetch service provider offers where user is either customer or provider (User Jobs)
      
      // First, let's check if the table has any data at all
      const { error: allOffersError } = await supabase
        .from('service_provider_offers')
        .select('*')
        .limit(5);
      
      if (allOffersError) {
        // Error checking table existence
      }
      
      const { data: offersData, error: offersError } = await supabase
        .from('service_provider_offers')
        .select('*')
        .or(`customer_id.eq.${userId},provider_id.eq.${userId}`)
        .order('created_at', { ascending: false });

      if (offersError) {
        setServiceOffers([]);
      } else {
        
        // If offers found, fetch job request details
        if (offersData && offersData.length > 0) {
          const jobIds = offersData.map(offer => offer.job_id);
          
          const { data: jobRequestsData, error: jobRequestsError } = await supabase
            .from('jobrequests')
            .select('id, title, user_id, budget, currency_code, budget_type, location')
            .in('id', jobIds);
          
          if (jobRequestsError) {
            // Still set offers even if job requests fail
            setServiceOffers(offersData);
          } else {
            
            // Create a map for quick lookup
            const jobRequestsMap = new Map();
            jobRequestsData?.forEach(job => {
              jobRequestsMap.set(job.id, job);
            });
            
            // Enrich offers with job request data
            const enrichedOffers = offersData.map(offer => {
              const jobRequest = jobRequestsMap.get(offer.job_id);
              return {
                ...offer,
                jobRequest: jobRequest
              };
            });
            setServiceOffers(enrichedOffers);
          }
        } else {
          setServiceOffers([]);
        }
        
        // If no offers found, let's check what customer_ids exist in the table
        if (!offersData || offersData.length === 0) {
          const { } = await supabase // eslint-disable-line no-empty-pattern
            .from('service_provider_offers')
            .select('customer_id, provider_id, offer_amount')
            .limit(10);
        }
      }

      // Fetch escrow payments related to user's offers
      const { data: escrowData, error: escrowError } = await supabase
        .from('jobservice_escrow_payments')
        .select('*')
        .order('created_at', { ascending: false });

      if (escrowError) {
        // Error fetching escrow payments
      } else {
        
        // Filter escrow payments related to user's offers
        if (escrowData && escrowData.length > 0) {
          const offerIds = escrowData.map(payment => payment.offer_id);
          const { data: offersForEscrow, error: offersError } = await supabase
            .from('service_provider_offers')
            .select('id, customer_id, provider_id')
            .in('id', offerIds);
          
          if (!offersError && offersForEscrow) {
            const userEscrowPayments = escrowData.filter(payment => {
              const offer = offersForEscrow.find(o => o.id === payment.offer_id);
              return offer && (offer.customer_id === userId || offer.provider_id === userId);
            });
            setEscrowPayments(userEscrowPayments);
          } else {
            setEscrowPayments(escrowData);
          }
        } else {
          setEscrowPayments([]);
        }
      }

    } catch (err) {
      setError('Error fetching user data');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'active':
      case 'completed':
      case 'accepted':
      case 'released':
        return 'bg-green-100 text-green-800';
      case 'pending':
      case 'processing':
        return 'bg-yellow-100 text-yellow-800';
      case 'cancelled':
      case 'rejected':
      case 'failed':
      case 'refunded':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatCurrency = (amount: string, currencyCode: string = 'USD') => {
    const numAmount = parseFloat(amount);
    if (isNaN(numAmount)) return 'Invalid amount';
    
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode
    }).format(numAmount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleBackToDashboard = () => {
    // Get dashboard state from location state or localStorage
    const dashboardState = location.state?.dashboardState || JSON.parse(localStorage.getItem('dashboardState') || '{}');
    
    if (dashboardState.activeSection) {
      // Navigate back to dashboard with preserved state
      navigate('/dashboard', { 
        state: { 
          preserveState: true,
          activeSection: dashboardState.activeSection,
          homeSearchTerm: dashboardState.homeSearchTerm,
          filteredUsers: dashboardState.filteredUsers,
          userManagementSearchTerm: dashboardState.userManagementSearchTerm,
          currentPage: dashboardState.currentPage
        }
      });
    } else {
      // Fallback to simple back navigation
      navigate(-1);
    }
  };

  const toggleOfferExpansion = (offerId: string) => {
    setExpandedOffers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(offerId)) {
        newSet.delete(offerId);
      } else {
        newSet.add(offerId);
      }
      return newSet;
    });
  };

  const toggleJobExpansion = (jobId: string) => {
    setExpandedJobs(prev => {
      const newSet = new Set(prev);
      if (newSet.has(jobId)) {
        newSet.delete(jobId);
      } else {
        newSet.add(jobId);
      }
      return newSet;
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading jobs & services...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-4">Error</h1>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-[#00a67e] shadow-sm border-b border-green-700">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-white">Jobs & Services</h1>
              <p className="text-green-100 mt-1">
                {userDisplayName}
              </p>
              <p className="text-green-200 text-sm mt-1">
                {userEmail || `User ID: ${userId}`}
              </p>
            </div>
            <button
              onClick={handleBackToDashboard}
              className="px-4 py-2 bg-white text-[#00a67e] rounded-lg hover:bg-green-50 transition-colors duration-200 font-medium"
            >
              ← Back to Dashboard
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Tab Navigation */}
        <div className="border-b border-gray-200 mb-8">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('listings')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'listings'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              User Listings ({filteredJobRequests.length}/{jobRequests.length})
            </button>
            <button
              onClick={() => setActiveTab('rentals')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'rentals'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              User Jobs ({filteredServiceOffers.length}/{serviceOffers.length})
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === 'listings' ? (
          /* User Listings Tab - Job Requests */
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50">
              <h2 className="text-lg font-semibold text-blue-800">Job Requests</h2>
              <p className="text-sm text-blue-600 mt-1">
                Jobs and services requested by this user
              </p>
              
              {/* Search Bar */}
              <div className="mt-4">
                <div className="relative max-w-md">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                  <input
                    type="text"
                    placeholder="Search job requests by title, category, location, status, or budget..."
                    value={listingsSearchTerm}
                    onChange={(e) => setListingsSearchTerm(e.target.value)}
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  />
                </div>
              </div>
            </div>
            
            {jobRequests.length === 0 ? (
              <div className="px-6 py-8 text-center text-gray-500">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <h3 className="mt-2 text-sm font-medium text-gray-900">No job requests found</h3>
                <p className="mt-1 text-sm text-gray-500">This user hasn't requested any jobs or services yet.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Job Details
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Budget
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Category
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Created
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Job Details
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredJobRequests.map((job) => (
                      <React.Fragment key={job.id}>
                        <tr className="hover:bg-gray-50">
                                                  <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="ml-0">
                              <div className="text-sm font-medium text-gray-900">{job.title}</div>
                              <div className="text-sm text-gray-500">{job.location || 'No location'}</div>
                              <div className="text-xs text-gray-400">{job.duration}</div>
                            </div>
                          </div>
                        </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {formatCurrency(job.budget, job.currency_code)}
                            <div className="text-xs text-gray-500">{job.budget_type}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            <div>{job.category}</div>
                            <div className="text-xs text-gray-400">{job.subcategory}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(job.status)}`}>
                              {job.status || 'Unknown'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatDate(job.created_at)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <button
                              onClick={() => toggleJobExpansion(job.id)}
                              className="flex items-center justify-center cursor-pointer p-2 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors duration-200"
                            >
                              <svg 
                                className={`w-4 h-4 text-blue-600 transition-transform duration-200 ${expandedJobs.has(job.id) ? 'rotate-180' : ''}`} 
                                fill="none" 
                                viewBox="0 0 24 24" 
                                stroke="currentColor"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                            </button>
                          </td>
                        </tr>
                        
                        {/* Job Details Row (expanded) */}
                        {expandedJobs.has(job.id) && (
                          <tr>
                            <td colSpan={6} className="px-6 py-4 bg-blue-50">
                              <div className="space-y-4">
                                <h4 className="text-sm font-medium text-blue-700 mb-3">Payment Details</h4>
                                
                                {/* Payment Details */}
                                <div className="bg-white rounded-lg p-3 border border-blue-200">
                                  <h5 className="text-xs font-medium text-blue-600 uppercase tracking-wider mb-2">Payment Details</h5>
                                  
                                  {/* Find related escrow payments for this job */}
                                  {(() => {
                                    // Find service offers for this job
                                    const relatedOffers = serviceOffers.filter(offer => offer.job_id === job.id);
                                    // Find escrow payments for those offers
                                    const relatedEscrowPayments = escrowPayments.filter(payment => 
                                      relatedOffers.some(offer => offer.id === payment.offer_id)
                                    );
                                    
                                    if (relatedEscrowPayments.length > 0) {
                                      // Show escrow payment data
                                      const payment = relatedEscrowPayments[0]; // Show first payment
                                      return (
                                        <>
                                          {/* Payment Intent ID and Status */}
                                          <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                                            <div className="flex items-center justify-between mb-2">
                                              <div className="text-sm font-medium text-gray-900">
                                                Payment Intent ID: {payment.payment_intent_id || 'N/A'}
                                              </div>
                                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(payment.status)}`}>
                                                {payment.status || 'Unknown'}
                                              </span>
                                            </div>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3 text-sm">
                                              <div>
                                                <span className="text-gray-600">Job ID: </span>
                                                <span className="font-medium text-xs">{job.id}</span>
                                              </div>
                                              <div>
                                                <span className="text-gray-600">Service Provider ID: </span>
                                                <span className="font-medium text-xs">
                                                  {(() => {
                                                    const relatedOffer = serviceOffers.find(offer => offer.job_id === job.id);
                                                    return relatedOffer?.provider_id || 'N/A';
                                                  })()}
                                                </span>
                                              </div>
                                            </div>
                                          </div>
                                          
                                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                                            {/* Amount Details */}
                                            <div className="bg-gray-50 rounded-lg p-3">
                                              <h6 className="text-xs font-medium text-gray-600 uppercase tracking-wider mb-2">Amount Details</h6>
                                              <div className="space-y-1">
                                                <div className="flex justify-between">
                                                  <span className="text-gray-600">Captured:</span>
                                                  <span className="font-medium">{formatCurrency(payment.amount_captured)}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                  <span className="text-gray-600">Offer:</span>
                                                  <span className="font-medium">{formatCurrency(payment.offer_amount)}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                  <span className="text-gray-600">Payout:</span>
                                                  <span className="font-medium">{formatCurrency(payment.payout_amount)}</span>
                                                </div>
                                              </div>
                                            </div>

                                            {/* Fee Breakdown */}
                                            <div className="bg-gray-50 rounded-lg p-3">
                                              <h6 className="text-xs font-medium text-gray-600 uppercase tracking-wider mb-2">Fee Breakdown</h6>
                                              <div className="space-y-1">
                                                <div className="flex justify-between">
                                                  <span className="text-gray-600">Requester:</span>
                                                  <span className="font-medium">{formatCurrency(payment.requester_fee)}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                  <span className="text-gray-600">Provider:</span>
                                                  <span className="font-medium">{formatCurrency(payment.provider_fee)}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                  <span className="text-gray-600">Platform:</span>
                                                  <span className="font-medium">{formatCurrency(payment.platform_fee)}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                  <span className="text-gray-600">Stripe:</span>
                                                  <span className="font-medium">{formatCurrency(payment.stripe_fee)}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                  <span className="text-gray-600">Revenue:</span>
                                                  <span className="font-medium">{formatCurrency(payment.platform_revenue)}</span>
                                                </div>
                                              </div>
                                            </div>

                                            {/* Transfer Information */}
                                            <div className="bg-gray-50 rounded-lg p-3">
                                              <h6 className="text-xs font-medium text-gray-600 uppercase tracking-wider mb-2">Transfer Details</h6>
                                              <div className="space-y-1">
                                                <div className="flex justify-between">
                                                  <span className="text-gray-600">Transfer ID:</span>
                                                  <span className="font-medium text-xs">
                                                    {payment.transfer_id || 'N/A'}
                                                  </span>
                                                </div>
                                                <div className="flex justify-between">
                                                  <span className="text-gray-600">Transfer Date:</span>
                                                  <span className="font-medium text-xs">
                                                    {payment.transfer_created_at ? formatDate(payment.transfer_created_at) : 'N/A'}
                                                  </span>
                                                </div>
                                              </div>
                                            </div>

                                            {/* Timeline & Status */}
                                            <div className="bg-gray-50 rounded-lg p-3">
                                              <h6 className="text-xs font-medium text-gray-600 uppercase tracking-wider mb-2">Timeline</h6>
                                              <div className="space-y-1">
                                                <div className="flex justify-between">
                                                  <span className="text-gray-600">Authorised:</span>
                                                  <span className="font-medium text-xs">
                                                    {payment.payment_authorised_at ? formatDate(payment.payment_authorised_at) : 'N/A'}
                                                  </span>
                                                </div>
                                                <div className="flex justify-between">
                                                  <span className="text-gray-600">Payment Captured:</span>
                                                  <span className="font-medium text-xs">
                                                    {payment.created_at ? formatDate(payment.created_at) : 'N/A'}
                                                  </span>
                                                </div>
                                                <div className="flex justify-between">
                                                  <span className="text-gray-600">Released:</span>
                                                  <span className="font-medium text-xs">
                                                    {payment.released_at ? formatDate(payment.released_at) : 'N/A'}
                                                  </span>
                                                </div>
                                                <div className="flex justify-between">
                                                  <span className="text-gray-600">Cancelled:</span>
                                                  <span className="font-medium text-xs">
                                                    {payment.cancelled_at ? formatDate(payment.cancelled_at) : 'N/A'}
                                                  </span>
                                                </div>
                                                <div className="flex justify-between">
                                                  <span className="text-gray-600">Refunded:</span>
                                                  <span className="font-medium text-xs">
                                                    {payment.refunded_at ? formatDate(payment.refunded_at) : 'N/A'}
                                                  </span>
                                                </div>
                                                <div className="flex justify-between">
                                                  <span className="text-gray-600">Currency Code:</span>
                                                  <span className="font-medium text-xs">{payment.currency_code || 'N/A'}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                  <span className="text-gray-600">Receipt URL:</span>
                                                  {payment.receipt_url ? (
                                                    <a href={payment.receipt_url} target="_blank" rel="noopener noreferrer" className="font-medium text-xs text-blue-600 hover:underline">
                                                      View Receipt
                                                    </a>
                                                  ) : (
                                                    <span className="font-medium text-xs">N/A</span>
                                                  )}
                                                </div>
                                                <div className="flex justify-between">
                                                  <span className="text-gray-600">New Payment Intent:</span>
                                                  <span className="font-medium text-xs">{payment.new_payment_intent_id || 'N/A'}</span>
                                                </div>
                                              </div>
                                            </div>
                                          </div>
                                          
                                          {/* Risk & Security Details */}
                                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 text-sm">
                                            <div className="bg-gray-50 rounded-lg p-3">
                                              <h6 className="text-xs font-medium text-gray-600 uppercase tracking-wider mb-2">Risk & Security</h6>
                                              <div className="space-y-1">
                                                <div className="flex justify-between">
                                                  <span className="text-gray-600">Risk Score:</span>
                                                  <span className="font-medium text-xs">{payment.risk_score !== undefined ? payment.risk_score : 'N/A'}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                  <span className="text-gray-600">Risk Level:</span>
                                                  <span className="font-medium text-xs">{payment.risk_level || 'N/A'}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                  <span className="text-gray-600">Authentication Method:</span>
                                                  <span className="font-medium text-xs">{payment.authentication_method || 'N/A'}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                  <span className="text-gray-600">Payment Method Type:</span>
                                                  <span className="font-medium text-xs">{payment.payment_method_type || 'N/A'}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                  <span className="text-gray-600">Card Brand:</span>
                                                  <span className="font-medium text-xs">{payment.card_brand || 'N/A'}</span>
                                                </div>
                                              </div>
                                            </div>

                                            {/* Location Details */}
                                            <div className="bg-gray-50 rounded-lg p-3">
                                              <h6 className="text-xs font-medium text-gray-600 uppercase tracking-wider mb-2">Location Details</h6>
                                              <div className="space-y-2">
                                                <div>
                                                  <div className="text-xs font-medium text-gray-700 mb-1">Requester Location:</div>
                                                  <div className="text-xs text-gray-600">{payment.requester_location_name || 'N/A'}</div>
                                                  {payment.requester_latitude && payment.requester_longitude && (
                                                    <div className="text-xs text-gray-500 mt-1">
                                                      Lat: {payment.requester_latitude}, Long: {payment.requester_longitude}
                                                    </div>
                                                  )}
                                                </div>
                                                <div>
                                                  <div className="text-xs font-medium text-gray-700 mb-1">Provider Location:</div>
                                                  <div className="text-xs text-gray-600">{payment.provider_location_name || 'N/A'}</div>
                                                  {payment.provider_latitude && payment.provider_longitude && (
                                                    <div className="text-xs text-gray-500 mt-1">
                                                      Lat: {payment.provider_latitude}, Long: {payment.provider_longitude}
                                                    </div>
                                                  )}
                                                </div>
                                              </div>
                                            </div>
                                          </div>
                                        </>
                                      );
                                    } else {
                                      // Show job budget data when no escrow payments
                                      return (
                                        <>
                                          {/* Payment Intent ID and Status */}
                                          <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                                            <div className="flex items-center justify-between mb-2">
                                              <div className="text-sm font-medium text-gray-900">
                                                Payment Intent ID: {job.id || 'N/A'}
                                              </div>
                                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(job.status)}`}>
                                                {job.status || 'Unknown'}
                                              </span>
                                            </div>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3 text-sm">
                                              <div>
                                                <span className="text-gray-600">Job ID: </span>
                                                <span className="font-medium text-xs">{job.id}</span>
                                              </div>
                                              <div>
                                                <span className="text-gray-600">Service Provider ID: </span>
                                                <span className="font-medium text-xs">
                                                  {(() => {
                                                    const relatedOffer = serviceOffers.find(offer => offer.job_id === job.id);
                                                    return relatedOffer?.provider_id || 'N/A';
                                                  })()}
                                                </span>
                                              </div>
                                            </div>
                                          </div>
                                          
                                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                                            {/* Amount Details */}
                                            <div className="bg-gray-50 rounded-lg p-3">
                                              <h6 className="text-xs font-medium text-gray-600 uppercase tracking-wider mb-2">Amount Details</h6>
                                              <div className="space-y-1">
                                                <div className="flex justify-between">
                                                  <span className="text-gray-600">Budget:</span>
                                                  <span className="font-medium">{formatCurrency(job.budget, job.currency_code)}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                  <span className="text-gray-600">Budget Type:</span>
                                                  <span className="font-medium">{job.budget_type || 'N/A'}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                  <span className="text-gray-600">Currency:</span>
                                                  <span className="font-medium">{job.currency_code || 'USD'}</span>
                                                </div>
                                              </div>
                                            </div>

                                            {/* Fee Breakdown */}
                                            <div className="bg-gray-50 rounded-lg p-3">
                                              <h6 className="text-xs font-medium text-gray-600 uppercase tracking-wider mb-2">Fee Breakdown</h6>
                                              <div className="space-y-1">
                                                <div className="flex justify-between">
                                                  <span className="text-gray-600">Requester Fee:</span>
                                                  <span className="font-medium">N/A</span>
                                                </div>
                                                <div className="flex justify-between">
                                                  <span className="text-gray-600">Provider Fee:</span>
                                                  <span className="font-medium">N/A</span>
                                                </div>
                                                <div className="flex justify-between">
                                                  <span className="text-gray-600">Platform Fee:</span>
                                                  <span className="font-medium">N/A</span>
                                                </div>
                                                <div className="flex justify-between">
                                                  <span className="text-gray-600">Stripe Fee:</span>
                                                  <span className="font-medium">N/A</span>
                                                </div>
                                                <div className="flex justify-between">
                                                  <span className="text-gray-600">Revenue:</span>
                                                  <span className="font-medium">N/A</span>
                                                </div>
                                              </div>
                                            </div>

                                            {/* Transfer Information */}
                                            <div className="bg-gray-50 rounded-lg p-3">
                                              <h6 className="text-xs font-medium text-gray-600 uppercase tracking-wider mb-2">Transfer Details</h6>
                                              <div className="space-y-1">
                                                <div className="flex justify-between">
                                                  <span className="text-gray-600">Transfer ID:</span>
                                                  <span className="font-medium text-xs">N/A</span>
                                                </div>
                                                <div className="flex justify-between">
                                                  <span className="text-gray-600">Transfer Date:</span>
                                                  <span className="font-medium text-xs">N/A</span>
                                                </div>
                                              </div>
                                            </div>

                                            {/* Timeline & Status */}
                                            <div className="bg-gray-50 rounded-lg p-3">
                                              <h6 className="text-xs font-medium text-gray-600 uppercase tracking-wider mb-2">Timeline</h6>
                                              <div className="space-y-1">
                                                <div className="flex justify-between">
                                                  <span className="text-gray-600">Created:</span>
                                                  <span className="font-medium text-xs">
                                                    {job.created_at ? formatDate(job.created_at) : 'N/A'}
                                                  </span>
                                                </div>
                                                <div className="flex justify-between">
                                                  <span className="text-gray-600">Scheduled:</span>
                                                  <span className="font-medium text-xs">
                                                    {job.scheduled_at ? formatDate(job.scheduled_at) : 'N/A'}
                                                  </span>
                                                </div>
                                                <div className="flex justify-between">
                                                  <span className="text-gray-600">Payment Captured:</span>
                                                  <span className="font-medium text-xs">N/A</span>
                                                </div>
                                                <div className="flex justify-between">
                                                  <span className="text-gray-600">Released:</span>
                                                  <span className="font-medium text-xs">N/A</span>
                                                </div>
                                                <div className="flex justify-between">
                                                  <span className="text-gray-600">Cancelled:</span>
                                                  <span className="font-medium text-xs">N/A</span>
                                                </div>
                                                <div className="flex justify-between">
                                                  <span className="text-gray-600">Refunded:</span>
                                                  <span className="font-medium text-xs">N/A</span>
                                                </div>
                                              </div>
                                            </div>
                                          </div>
                                        </>
                                      );
                                    }
                                  })()}
                                  
                                  {/* Payment Preferences */}
                                  <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                                    <h6 className="text-xs font-medium text-gray-600 uppercase tracking-wider mb-2">Payment Preferences</h6>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                                      <div className="flex justify-between">
                                        <span className="text-gray-600">Payment Method:</span>
                                        <span className="font-medium">{job.payment_method || 'N/A'}</span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-gray-600">Accept Cash:</span>
                                        <span className="font-medium">{job.accept_cash ? 'Yes' : 'No'}</span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-gray-600">Accept Secure:</span>
                                        <span className="font-medium">{job.accept_secure ? 'Yes' : 'No'}</span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ) : (
          /* My Rentals Tab - Service Offers and Escrow Payments */
          <div className="space-y-6">
            {/* Service Offers Section */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="px-6 py-4 border-b border-purple-200 bg-gradient-to-r from-purple-50 to-pink-50">
                <h3 className="text-lg font-semibold text-purple-800">Service Offers</h3>
                
                {/* Search Bar */}
                <div className="mt-4">
                  <div className="relative max-w-md">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                    <input
                      type="text"
                      placeholder="Search service offers by job title, status, user ID, or amount..."
                      value={jobsSearchTerm}
                      onChange={(e) => setJobsSearchTerm(e.target.value)}
                      className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    />
                  </div>
                </div>
              </div>
              
              {serviceOffers.length === 0 ? (
                <div className="px-6 py-8 text-center text-gray-500">
                  <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                  </svg>
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No service offers found</h3>
                  <p className="mt-1 text-sm text-gray-500">This user hasn't received any service offers yet.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Job Request
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Offer Amount
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Location
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Created
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Offer Details
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredServiceOffers.map((offer) => {
                        // Find related escrow payments for this offer
                        const relatedPayments = escrowPayments.filter(payment => payment.offer_id === offer.id);
                        
                        return (
                          <React.Fragment key={offer.id}>
                            <tr className="hover:bg-gray-50">
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm font-medium text-gray-900">
                                  {offer.jobRequest?.title || 'Job title not found'}
                                </div>
                                <div className="text-sm text-gray-500">
                                  Requester ID: {offer.customer_id}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {formatCurrency(offer.offer_amount)}
                                <div className="text-xs text-gray-500 mt-1">
                                  Listing Budget: {offer.jobRequest?.budget || 'N/A'} {offer.jobRequest?.currency_code || 'USD'} {offer.jobRequest?.budget_type || 'Fixed'}
                                </div>
                              </td>
                              <td className="px-6 py-4 text-sm text-gray-500">
                                <div className="max-w-xs break-words">{offer.jobRequest?.location || 'No location'}</div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(offer.status)}`}>
                                  {offer.status || 'Unknown'}
                                </span>
                              </td>
                              <td className="px-6 py-4 text-sm text-gray-500">
                                <div className="max-w-xs break-words">{formatDate(offer.created_at)}</div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <button
                                  onClick={() => toggleOfferExpansion(offer.id)}
                                  className="flex items-center justify-center cursor-pointer p-2 bg-green-50 rounded-lg hover:bg-green-100 transition-colors duration-200"
                                >
                                  <svg 
                                    className={`w-4 h-4 text-green-600 transition-transform duration-200 ${expandedOffers.has(offer.id) ? 'rotate-180' : ''}`} 
                                    fill="none" 
                                    viewBox="0 0 24 24" 
                                    stroke="currentColor"
                                  >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                  </svg>
                                </button>
                              </td>
                            </tr>
                            
                            {/* Offer Details Row (expanded) */}
                            {expandedOffers.has(offer.id) && (
                              <tr>
                                <td colSpan={6} className="px-6 py-4 bg-blue-50">
                                  <div className="space-y-4">
                                    <h4 className="text-sm font-medium text-blue-700 mb-3">Service Offer Details</h4>
                                    
                                    {/* Basic Offer Information */}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                                      {/* Basic Offer Information */}
                                      <div className="bg-white rounded-lg p-3 border border-blue-200">
                                        <h5 className="text-xs font-medium text-blue-600 uppercase tracking-wider mb-2">Basic Information</h5>
                                        <div className="space-y-1">
                                          <div className="flex justify-between">
                                            <span className="text-gray-600">Offer ID:</span>
                                            <span className="font-medium text-xs">{offer.id}</span>
                                          </div>
                                          <div className="flex justify-between">
                                            <span className="text-gray-600">Job ID:</span>
                                            <span className="font-medium text-xs">{offer.job_id}</span>
                                          </div>
                                          <div className="flex justify-between">
                                            <span className="text-gray-600">Customer ID:</span>
                                            <span className="font-medium text-xs">{offer.customer_id}</span>
                                          </div>
                                          <div className="flex justify-between">
                                            <span className="text-gray-600">Offer Type:</span>
                                            <span className="font-medium">{offer.offer_type}</span>
                                          </div>
                                          <div className="flex justify-between">
                                            <span className="text-gray-600">Client Secret:</span>
                                            <span className="font-medium text-xs">
                                              {offer.client_secret || 'N/A'}
                                            </span>
                                          </div>
                                        </div>
                                      </div>

                                      {/* Project Details */}
                                      <div className="bg-white rounded-lg p-3 border border-blue-200">
                                        <h5 className="text-xs font-medium text-blue-600 uppercase tracking-wider mb-2">Project Details</h5>
                                        <div className="space-y-1">
                                          <div className="flex justify-between">
                                            <span className="text-gray-600">Timeline:</span>
                                            <span className="font-medium">{offer.timeline}</span>
                                          </div>
                                          <div className="flex justify-between">
                                            <span className="text-gray-600">Payment Type:</span>
                                            <span className="font-medium">{offer.offer_payment_type}</span>
                                          </div>
                                          <div className="flex justify-between">
                                            <span className="text-gray-600">Start Date:</span>
                                            <span className="font-medium text-xs">
                                              {offer.start_date ? formatDate(offer.start_date) : 'N/A'}
                                            </span>
                                          </div>
                                          <div className="flex justify-between">
                                            <span className="text-gray-600">Completion:</span>
                                            <span className="font-medium text-xs">
                                              {offer.completion_date ? formatDate(offer.completion_date) : 'N/A'}
                                            </span>
                                          </div>

                                          <div className="flex justify-between">
                                            <span className="text-gray-600">Guarantee:</span>
                                            <span className="font-medium">{offer.guarantee_period || 'N/A'}</span>
                                          </div>
                                          <div className="flex justify-between">
                                            <span className="text-gray-600">Materials:</span>
                                            <span className="font-medium">{offer.includes_materials ? 'Yes' : 'No'}</span>
                                          </div>
                                          {offer.materials_cost && (
                                            <div className="flex justify-between">
                                              <span className="text-gray-600">Materials Cost:</span>
                                              <span className="font-medium">{formatCurrency(offer.materials_cost)}</span>
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    </div>

                                    {/* Additional Information Row */}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">




                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )}

                            {/* Escrow Payments Row (expanded) */}
                            {relatedPayments.length > 0 && expandedOffers.has(offer.id) && (
                              <tr>
                                <td colSpan={6} className="px-6 py-4 bg-purple-50">
                                  <div className="space-y-3">
                                    <h4 className="text-sm font-medium text-gray-700 mb-3">Payment Details</h4>
                                    {relatedPayments.map((payment) => (
                                      <div key={payment.id} className="p-4 bg-white border border-gray-200 rounded-lg">
                                        <div className="flex items-center justify-between mb-3">
                                          <div className="text-sm font-medium text-gray-900">
                                            Payment Intent ID: {payment.payment_intent_id}
                                          </div>
                                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(payment.status)}`}>
                                            {payment.status || 'Unknown'}
                                          </span>
                                        </div>
                                        
                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                                          {/* Amount Details */}
                                          <div className="bg-gray-50 rounded-lg p-3">
                                            <h5 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">Amount Details</h5>
                                            <div className="space-y-1">
                                              <div className="flex justify-between">
                                                <span className="text-gray-600">Captured:</span>
                                                <span className="font-medium">{formatCurrency(payment.amount_captured)}</span>
                                              </div>
                                              <div className="flex justify-between">
                                                <span className="text-gray-600">Offer:</span>
                                                <span className="font-medium">{formatCurrency(payment.offer_amount)}</span>
                                              </div>
                                              <div className="flex justify-between">
                                                <span className="text-gray-600">Payout:</span>
                                                <span className="font-medium">{formatCurrency(payment.payout_amount)}</span>
                                              </div>
                                            </div>
                                          </div>

                                          {/* Fee Breakdown */}
                                          <div className="bg-gray-50 rounded-lg p-3">
                                            <h5 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">Fee Breakdown</h5>
                                            <div className="space-y-1">
                                              <div className="flex justify-between">
                                                <span className="text-gray-600">Requester:</span>
                                                <span className="font-medium">{formatCurrency(payment.requester_fee)}</span>
                                              </div>
                                              <div className="flex justify-between">
                                                <span className="text-gray-600">Provider:</span>
                                                <span className="font-medium">{formatCurrency(payment.provider_fee)}</span>
                                              </div>
                                              <div className="flex justify-between">
                                                <span className="text-gray-600">Platform:</span>
                                                <span className="font-medium">{formatCurrency(payment.platform_fee)}</span>
                                              </div>
                                              <div className="flex justify-between">
                                                <span className="text-gray-600">Stripe:</span>
                                                <span className="font-medium">{formatCurrency(payment.stripe_fee)}</span>
                                              </div>
                                              <div className="flex justify-between">
                                                <span className="text-gray-600">Revenue:</span>
                                                <span className="font-medium">{formatCurrency(payment.platform_revenue)}</span>
                                              </div>
                                            </div>
                                          </div>

                                          {/* Transfer Information */}
                                          <div className="bg-gray-50 rounded-lg p-3">
                                            <h5 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">Transfer Details</h5>
                                            <div className="space-y-1">
                                              <div className="flex justify-between">
                                                <span className="text-gray-600">Transfer ID:</span>
                                                <span className="font-medium text-xs">
                                                  {payment.transfer_id || 'N/A'}
                                                </span>
                                              </div>
                                              <div className="flex justify-between">
                                                <span className="text-gray-600">Transfer Date:</span>
                                                <span className="font-medium text-xs">
                                                  {payment.transfer_created_at ? formatDate(payment.transfer_created_at) : 'N/A'}
                                                </span>
                                              </div>
                                            </div>
                                          </div>

                                          {/* Timeline & Status */}
                                          <div className="bg-gray-50 rounded-lg p-3">
                                            <h5 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">Timeline</h5>
                                            <div className="space-y-1">
                                              <div className="flex justify-between">
                                                <span className="text-gray-600">Authorised:</span>
                                                <span className="font-medium text-xs">
                                                  {payment.payment_authorised_at ? formatDate(payment.payment_authorised_at) : 'N/A'}
                                                </span>
                                              </div>
                                              <div className="flex justify-between">
                                                <span className="text-gray-600">Payment Captured:</span>
                                                <span className="font-medium text-xs">
                                                  {offer.payment_captured_at ? formatDate(offer.payment_captured_at) : 'N/A'}
                                                </span>
                                              </div>
                                              <div className="flex justify-between">
                                                <span className="text-gray-600">Released:</span>
                                                <span className="font-medium text-xs">
                                                  {payment.released_at ? formatDate(payment.released_at) : 'N/A'}
                                                </span>
                                              </div>
                                              <div className="flex justify-between">
                                                <span className="text-gray-600">Cancelled:</span>
                                                <span className="font-medium text-xs">
                                                  {payment.cancelled_at ? formatDate(payment.cancelled_at) : 'N/A'}
                                                </span>
                                              </div>
                                              <div className="flex justify-between">
                                                <span className="text-gray-600">Refunded:</span>
                                                <span className="font-medium text-xs">
                                                  {payment.refunded_at ? formatDate(payment.refunded_at) : 'N/A'}
                                                </span>
                                              </div>
                                              <div className="flex justify-between">
                                                <span className="text-gray-600">Currency Code:</span>
                                                <span className="font-medium text-xs">{payment.currency_code || 'N/A'}</span>
                                              </div>
                                              <div className="flex justify-between">
                                                <span className="text-gray-600">Receipt URL:</span>
                                                {payment.receipt_url ? (
                                                  <a href={payment.receipt_url} target="_blank" rel="noopener noreferrer" className="font-medium text-xs text-blue-600 hover:underline">
                                                    View Receipt
                                                  </a>
                                                ) : (
                                                  <span className="font-medium text-xs">N/A</span>
                                                )}
                                              </div>
                                              <div className="flex justify-between">
                                                <span className="text-gray-600">New Payment Intent:</span>
                                                <span className="font-medium text-xs">{payment.new_payment_intent_id || 'N/A'}</span>
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                        
                                        {/* Risk & Security Details */}
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 text-sm">
                                          <div className="bg-gray-50 rounded-lg p-3">
                                            <h5 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">Risk & Security</h5>
                                            <div className="space-y-1">
                                              <div className="flex justify-between">
                                                <span className="text-gray-600">Risk Score:</span>
                                                <span className="font-medium text-xs">{payment.risk_score !== undefined ? payment.risk_score : 'N/A'}</span>
                                              </div>
                                              <div className="flex justify-between">
                                                <span className="text-gray-600">Risk Level:</span>
                                                <span className="font-medium text-xs">{payment.risk_level || 'N/A'}</span>
                                              </div>
                                              <div className="flex justify-between">
                                                <span className="text-gray-600">Authentication Method:</span>
                                                <span className="font-medium text-xs">{payment.authentication_method || 'N/A'}</span>
                                              </div>
                                              <div className="flex justify-between">
                                                <span className="text-gray-600">Payment Method Type:</span>
                                                <span className="font-medium text-xs">{payment.payment_method_type || 'N/A'}</span>
                                              </div>
                                              <div className="flex justify-between">
                                                <span className="text-gray-600">Card Brand:</span>
                                                <span className="font-medium text-xs">{payment.card_brand || 'N/A'}</span>
                                              </div>
                                            </div>
                                          </div>

                                          {/* Location Details */}
                                          <div className="bg-gray-50 rounded-lg p-3">
                                            <h5 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">Location Details</h5>
                                            <div className="space-y-2">
                                              <div>
                                                <div className="text-xs font-medium text-gray-700 mb-1">Requester Location:</div>
                                                <div className="text-xs text-gray-600">{payment.requester_location_name || 'N/A'}</div>
                                                {payment.requester_latitude && payment.requester_longitude && (
                                                  <div className="text-xs text-gray-500 mt-1">
                                                    Lat: {payment.requester_latitude}, Long: {payment.requester_longitude}
                                                  </div>
                                                )}
                                              </div>
                                              <div>
                                                <div className="text-xs font-medium text-gray-700 mb-1">Provider Location:</div>
                                                <div className="text-xs text-gray-600">{payment.provider_location_name || 'N/A'}</div>
                                                {payment.provider_latitude && payment.provider_longitude && (
                                                  <div className="text-xs text-gray-500 mt-1">
                                                    Lat: {payment.provider_latitude}, Long: {payment.provider_longitude}
                                                  </div>
                                                )}
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserJobsServices;
